import { Injectable, NgZone } from '@angular/core';
import {
  QUOTE_FILL_DESCRIPTION, QUOTE_INPUT_SCHEMA, QUOTE_STEP_DESCRIPTION, QUOTE_TOOL_FILL, QUOTE_TOOL_STEP, QuoteFillResult, QuoteFormAdapter,
  describeQuoteStep, runQuoteFill,
} from './quote-tool';

/**
 * FRU-23700: registers the two quote-form tools with the browser's WebMCP model context, once per page, and sends each call to
 * whichever form arm is on screen. The original form sets itself as the base; Hope, while mounted, takes over and gives it back
 * when it closes. Browsers without WebMCP get nothing registered and nothing runs.
 */
@Injectable({ providedIn: 'root' })
export class QuoteToolService {
  private base: QuoteFormAdapter | null = null;
  private active: QuoteFormAdapter | null = null;
  private registered = false;

  constructor(private zone: NgZone) { }

  /** The form arm the tools answer now. */
  get adapter(): QuoteFormAdapter | null { return this.active || this.base; }

  /** The original form, which stays underneath Hope. */
  setBase(adapter: QuoteFormAdapter | null): void { this.base = adapter; this.register(); }

  /** Hope, while it is on screen. */
  use(adapter: QuoteFormAdapter): void { this.active = adapter; this.register(); }

  release(adapter: QuoteFormAdapter): void { if (this.active === adapter) { this.active = null; } }

  /** The original form going away (a route change). */
  clearBase(adapter: QuoteFormAdapter): void { if (this.base === adapter) { this.base = null; } }

  /** The browser's model context: document.modelContext per the spec, navigator.modelContext in Chrome's early preview. */
  static modelContext(): any {
    try {
      const doc: any = typeof document !== 'undefined' ? document : null;
      const nav: any = typeof navigator !== 'undefined' ? navigator : null;
      const mc = (doc && doc.modelContext) || (nav && nav.modelContext) || null;
      return mc && typeof mc.registerTool === 'function' ? mc : null;
    } catch (e) { return null; }
  }

  private register(): void {
    if (this.registered) { return; }
    const mc = QuoteToolService.modelContext();
    if (!mc) { return; }
    this.registered = true;
    const add = (tool: any) => {
      try { const p = mc.registerTool(tool); if (p && typeof p.catch === 'function') { p.catch(() => undefined); } } catch (e) { /* the page works without it */ }
    };
    add({
      name: QUOTE_TOOL_STEP,
      title: 'Current rate request question',
      description: QUOTE_STEP_DESCRIPTION,
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true },
      execute: () => this.zone.run(() => Promise.resolve(describeQuoteStep(this.adapter))),
    });
    add({
      name: QUOTE_TOOL_FILL,
      title: 'Fill in the rate request',
      description: QUOTE_FILL_DESCRIPTION,
      inputSchema: QUOTE_INPUT_SCHEMA,
      annotations: { readOnlyHint: false },
      execute: (input: any, options?: any) => this.zone.run(() => this.fill(input, options)),
    });
  }

  /** One fill at a time: a second call while one runs could answer a question the first has already moved past. */
  private filling = false;

  private fill(input: any, options?: any): Promise<QuoteFillResult> {
    const adapter = this.adapter;
    if (!adapter) { return Promise.resolve({ status: 'unavailable', answered: [], message: 'The quote form is not on screen right now.' }); }
    if (this.filling) {
      return Promise.resolve({ status: 'busy', arm: adapter.arm, answered: [], message: 'Another fill is still running. Call again when it returns.' });
    }
    const answers = input && typeof input === 'object' && input.answers && typeof input.answers === 'object' ? input.answers : {};
    this.filling = true;
    return runQuoteFill(adapter, answers, { signal: options && options.signal ? options.signal : null })
      .finally(() => { this.filling = false; });
  }
}
